// Package Imports
import React, { useState } from 'react';
import { FunctionComponent } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import classNames from 'classnames';
import { useMediaQuery } from 'react-responsive';
import { useSession } from 'next-auth/react';
import { v4 as uuidv4 } from 'uuid';

// Function Imports
import { isPriceValid } from '@/lib/functions/isPriceValid';
import { isProductShippable } from '@/lib/functions/isProductShippable';
import { defaultStoreNumber } from '@/lib/constants/store';

// Constants Imports
import { PlaceholderImageWhite } from '@/lib/constants/common';

// Component Imports
import AddToCartForm from '@/components/product/addToCartForm';
import AddToCartMobile from '@/components/product/addToCartMobile';
import ProductPrice from '@/components/product/productPrice';
import StylizedButtonLink from '@/components/stylizedButtonLink';
import Toast from '@/components/toast';
import ProductShippingDebug from '@/components/product/productShippingDebug';

// Type Imports
import type { NormalizedProduct } from '@/lib/types/products/normalizedProduct';

// Style Imports
import styles from './productCard.module.scss';

// Define Interfaces
interface ToastState {
  id: string;
  type: 'error' | 'success' | 'notice';
  message: string;
}

// Define Props
interface ProductCardProps {
  product: NormalizedProduct;
  layout?: 'vertical' | 'horizontal';
  showBorder?: boolean;
  hideQuantityField?: boolean;
  toastEnabled?: boolean;
  loading?: boolean;
}

/**
 * Renders a product card used across search results, recommendations, carousels, etc.
 * Supports various layouts (default, featured, horizontal, etc.) and skeleton loading state.
 */
const ProductCard: FunctionComponent<ProductCardProps> = ({
  product,
  layout = 'vertical',
  showBorder = false,
  hideQuantityField = false,
  toastEnabled = true,
  loading = false,
}) => {
  const { data: session } = useSession();
  const isLoggedIn = session && session.user;
  const [alerts, setToasts] = useState<ToastState[]>([]);
  const isTablet = useMediaQuery({ query: '(max-width: 991px)' });
  const isShippable = isProductShippable(product?.shippingCategory, product?.shippingDimensions);

  const cardShippingDebug = {
    defaultStoreNumber,
    code: product?.code ?? null,
    shippingCategory: product?.shippingCategory ?? null,
    shippingDimensions: product?.shippingDimensions ?? null,
    isShippable,
    inStock: product?.stock?.inStock ?? null,
    unitPrice: product?.pricing?.unitPrice ?? null,
    showAddToCart: Boolean(product?.stock?.inStock && isPriceValid(product?.pricing?.unitPrice) && isShippable),
  };

  const showToast = (type: 'success' | 'error' | 'notice', message: string) => {
    if (!toastEnabled) return;

    const newToast = { id: uuidv4(), type, message };
    setToasts(prev => [...prev, newToast]);
    setTimeout(() => setToasts(prev => prev.filter(toast => toast.id !== newToast.id)), 5000);
  };

  const handleDismiss = (id: string) => {
    setToasts(prev => prev.filter(alert => alert.id !== id));
  };

  return (
    <div className={styles.cardWrapper}>
      <div className={classNames(styles.innerWrapper, {
        [styles.imageAboveInfo]: layout === 'vertical',
        [styles.showBorder]: showBorder,
      })}>
        {/* Image */}
        <div className={classNames([styles.image])}>
          {loading || product.url == null ? (
            <div className={styles.skeletonImage} />
          ) : (
            <div className={styles.imageWrapper}>
              <Link href={product.url ?? '#'}>
                <Image
                  src={product?.details?.image || PlaceholderImageWhite}
                  alt={product?.details.title || ''}
                  blurDataURL={PlaceholderImageWhite}
                  width={250}
                  height={250}
                  quality={75}
                />
              </Link>
            </div>
          )}
        </div>

        <div className={classNames([styles.infoWrapper])}>
          {/* Product Info */}
          <div className={styles.productInfo}>

            {/* Title */}
            {loading ? (
              <div className={styles.skeletonTitle} />
            ) : (
              <div className={classNames([styles.info])}>
                <h3 className={classNames(styles.title, {
                  [styles.centerText]: layout === 'vertical',
                })}>
                  {product.url ? (
                    <Link href={product.url}>
                      {product?.details?.title}
                    </Link>
                  ) : (
                    <>{product?.details?.title}</>
                  )}
                </h3>
              </div>
            )}

            {/* Excerpts */}
            {loading ? (
              <>
                <div className={styles.skeletonSize} />
                <div className={styles.skeletonPrice} />
              </>
            ) : (
              product && product?.pricing?.unitPrice && (
                <div className={classNames(styles.excerpts, {
                  [styles.centerText]: layout === 'vertical',
                })}>
                  <ProductPrice
                    unitPrice={product?.pricing?.unitPrice}
                    unitPricePromoDiscount={product?.pricing?.unitPricePromoDiscount || 0}
                    unitPriceKeyclubDiscount={product?.pricing?.unitPriceKeyclubDiscount || 0}
                    casePrice={product?.pricing?.casePrice || product?.pricing?.unitPrice}
                    caseQuantity={product?.pricing?.caseQuantity || 1}
                    casePricePromoDiscount={product?.pricing?.casePricePromoDiscount || 0}
                    casePriceKeyclubDiscount={product?.pricing?.casePriceKeyclubDiscount || 0}
                    isKeyclubMember={isLoggedIn}
                    className="productCardPrice"
                  />
                </div>
              )
            )}

          </div>

          {/* Buttons */}
          {loading ? (
            <div className={styles.skeletonButtons}>
              <span className={styles.skeletonButtonCart} />
            </div>
          ) : (
            <div className={classNames([styles.buttonWrapper])}>
              {product?.stock?.inStock && isPriceValid(product?.pricing?.unitPrice) && isShippable ? (
                isTablet && !['horizontal'].includes(layout || '') ? (
                  <AddToCartMobile
                    code={product.code || ''}
                    title={product?.details?.title || ''}
                    showToast={showToast}
                    className="productCard"
                  />
                ) : (
                  <AddToCartForm
                    code={product?.code}
                    label="ADD TO CART"
                    title={product?.details?.title || ''}
                    url={product?.url || '#'}
                    price={product?.pricing?.unitPrice || 0}
                    showToast={showToast}
                    hideQuantityField={hideQuantityField}
                    classNameWrapper={styles.formWrapper}
                    classNameButton={styles.button}
                    classNameInput={styles.input}
                  />
                )
              ) : (
                <div className={styles.unavailableWrapper}>
                  {!isShippable ? (
                    <StylizedButtonLink href={product.url ?? '#'}>
                      VIEW PRODUCT
                    </StylizedButtonLink>
                  ) : (
                    <StylizedButtonLink href="#" disabled>
                      Out of Stock
                    </StylizedButtonLink>
                  )}
                </div>
              )}
            </div>
          )}

          <ProductShippingDebug label="Card shipping debug (dev or ?debugProduct=1)" data={cardShippingDebug} />
        </div>

      </div>

      {/* Toast Alerts */}
      {alerts.map((alert) => (
        <div className={classNames(styles.toastWrapper)} key={alert.id}>
          <Toast
            id={alert.id}
            type={alert.type}
            message={alert.message}
            onDismiss={() => handleDismiss(alert.id)}
            dismissible
            fadeOutTime={5000}
          />
        </div>
      ))}
    </div>
  );
};

export default ProductCard;
